import moment from "moment";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useLoadImage from "../../../../../hooks/useLoadImage";
import Modal from "../../../../../parts/modal-new";
import { progressPhotoCommentActions } from "../../../../../parts/modals/thread-comments/comment-action-configs";
import { CommentsSection } from "../../../../../parts/modals/thread-comments/comments-section";

const PreviewPhotoModal = ({
  photo,
  threadId,
  locals,
  dailyStatId,
  utcDate,
}) => {
  const { t } = useTranslation("athlete-portal");
  const { type, pictureImg } = photo || {};
  const imgSrc = useLoadImage({ src: pictureImg });
  const bodyRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Function to download the photo
   * @param {*} e 
   * @returns 
   */
  const handleDownload = async (e) => {
    e.preventDefault();
    if (!pictureImg || isDownloading) return;
    setIsDownloading(true);
    const sanitize = (str) => (str || "").replace(/[^\w\s-]/g, "_").trim();
    const datePart = utcDate
      ? moment(utcDate, "YYYYMMDD").format("YYYY-MM-DD")
      : "";
    const fileName = [
      sanitize(locals?.athlete?.name),
      sanitize(type) || "progress-photo",
      datePart,
    ]
      .filter(Boolean)
      .join("_");
    const triggerDownload = (href, name) => {
      const link = document.createElement("a");
      link.href = href;
      if (name) link.download = name;
      else {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
      }
      document.body.appendChild(link);
      link.click();
      link.remove();
    };

    try {
      const apiBase = globalThis.env?.REACT_APP_API_URL ?? "";
      const proxyUrl = `${apiBase}/api/download-photo?url=${encodeURIComponent(pictureImg)}&auth=${encodeURIComponent(locals?.user?.token)}`;
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      triggerDownload(url, `${fileName}.png`);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      // Last-resort fallback: open in a new tab so the user can save manually.
      triggerDownload(pictureImg);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal
      className="preview-photo-modal"
      title={type || "Photo"}
      toClear={[
        "threadId",
        "pictureImg",
        "photoType",
        "dailyStatId",
        "utcDate",
      ]}
    >
      <div className="body" ref={bodyRef}>
        <div className="photo-container">
          <div className="photo-wrapper">
            <img alt={type || "Progress photo"} src={imgSrc} />
            <button
              type="button"
              onClick={handleDownload}
              data-button-type="tertiary"
              data-icon-only="true"
              aria-busy={isDownloading}
              title={t("progressPhotos.download")}
              className="absolute top-2 right-2"
            >
              {!isDownloading && <img src="/img/icon-download.webp" alt="download" className="preview-photo-download-icon" />}
            </button>
          </div>
        </div>
        <div className="comments-section-wrapper">
          <CommentsSection
            bodyRef={bodyRef}
            commentActions={progressPhotoCommentActions}
            commentParams={{
              threadId,
              photoType: type,
              dailyStatId,
              receiverId: locals?.athlete?._id,
              utcDate,
            }}
            locals={locals}
          />
        </div>
      </div>
    </Modal>
  );
};

PreviewPhotoModal.propTypes = {
  photo: PropTypes.shape({
    type: PropTypes.string,
    pictureImg: PropTypes.string,
  }),
  threadId: PropTypes.string,
  locals: PropTypes.shape({
    athlete: PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
    }),
    user: PropTypes.shape({
      token: PropTypes.string,
    }),
  }),
  dailyStatId: PropTypes.string,
  utcDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default PreviewPhotoModal;
